import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Receipt, Gift, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

interface FeesConfig {
  maker_fee_bps: string;
  taker_fee_bps: string;
  maker_fee_percentage: string;
  taker_fee_percentage: string;
}

export const Fees = () => {
  const [fees, setFees] = useState<FeesConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchFees() {
      try {
        const res = await fetch(`${API_URL}/api/config/fees`);
        if (!res.ok) throw new Error("Failed");
        const data: FeesConfig = await res.json();
        setFees(data);
      } catch {
        // Fall back to null - will show defaults
      } finally {
        setLoading(false);
      }
    }
    fetchFees();
  }, []);

  // Use live data or sensible fallbacks
  const makerPct = fees ? parseFloat(fees.maker_fee_percentage) : 0.02;
  const takerPct = fees ? parseFloat(fees.taker_fee_percentage) : 0.05;
  const makerBps = fees ? parseInt(fees.maker_fee_bps) : 2;
  const takerBps = fees ? parseInt(fees.taker_fee_bps) : 5;

  // Referral discount is 10%
  const makerDiscounted = (makerPct * 0.9).toFixed(3);
  const takerDiscounted = (takerPct * 0.9).toFixed(3);

  // Example calculations
  const exampleSize = 10000;
  const takerFee = (exampleSize * takerPct) / 100;
  const takerFeeDiscounted = (exampleSize * takerPct * 0.9) / 100;
  const smallSize = 500;
  const smallTakerFee = (smallSize * takerPct) / 100;

  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Fee Structure</h1>

        <div className="text-sm text-muted-foreground mb-8">
          <p>
            Understand trading fees, referral discounts, and how fees are
            calculated
          </p>
        </div>

        {/* Trading Fees */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Trading Fees</h2>

          <p className="text-muted-foreground mb-6">
            Nordex charges trading fees on each order based on the position
            size. Fees are deducted from your collateral when you open or
            close a position.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading fee data...</span>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-6 border border-border rounded-lg bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <Receipt className="h-6 w-6 text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-lg">Maker Fee</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Charged when your order adds liquidity to the order book
                  (limit orders that rest on the book before filling).
                </p>
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-500">
                    {makerPct}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {makerBps} basis points
                  </div>
                </div>
              </div>

              <div className="p-6 border border-border rounded-lg bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Receipt className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Taker Fee</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Charged when your order removes liquidity from the order
                  book (market orders and limit orders that fill
                  immediately).
                </p>
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="text-2xl font-bold text-primary">
                    {takerPct}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {takerBps} basis points
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Fee Examples */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Fee Calculation Examples
          </h2>

          <div className="space-y-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-4">
                Example 1: Opening a Position
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Collateral:</span>
                  <span className="font-semibold">$1,000</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Leverage:</span>
                  <span className="font-semibold">10x</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">
                    Position Size:
                  </span>
                  <span className="font-semibold">
                    ${exampleSize.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Order Type:</span>
                  <span className="font-semibold">
                    Market Order (Taker)
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <span className="font-semibold">Trading Fee:</span>
                  <span className="font-bold text-primary">
                    ${takerFee.toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Calculation: ${exampleSize.toLocaleString()} x {takerPct}% = $
                {takerFee.toFixed(2)}
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-4">
                Example 2: Closing a Position
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">
                    Position Size:
                  </span>
                  <span className="font-semibold">
                    ${exampleSize.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Order Type:</span>
                  <span className="font-semibold">
                    Market Order (Taker)
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <span className="font-semibold">Trading Fee:</span>
                  <span className="font-bold text-primary">
                    ${takerFee.toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                <strong>Total Round-Trip Cost:</strong> $
                {takerFee.toFixed(2)} (open) + ${takerFee.toFixed(2)}{" "}
                (close) = ${(takerFee * 2).toFixed(2)}
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-4">
                Example 3: Smaller Position
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Collateral:</span>
                  <span className="font-semibold">$100</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">Leverage:</span>
                  <span className="font-semibold">5x</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                  <span className="text-muted-foreground">
                    Position Size:
                  </span>
                  <span className="font-semibold">
                    ${smallSize.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <span className="font-semibold">Trading Fee:</span>
                  <span className="font-bold text-primary">
                    ${smallTakerFee.toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Calculation: ${smallSize.toLocaleString()} x {takerPct}% = $
                {smallTakerFee.toFixed(2)}
              </p>
            </div>
          </div>
        </section>

        {/* Referral Discounts */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Referral Fee Discounts
          </h2>

          <div className="p-6 border border-emerald-500/20 rounded-lg bg-emerald-500/5 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Gift className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-lg">10% Fee Discount</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              If you signed up using a referral link, you receive a
              permanent 10% discount on all trading fees.
            </p>
          </div>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold mb-4">With Referral Discount:</h3>
            <div className="grid gap-3 md:grid-cols-2 text-sm">
              <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <div className="text-muted-foreground mb-1">Maker Fee</div>
                <div className="text-lg font-bold text-emerald-500">
                  {makerDiscounted}%
                </div>
                <div className="text-xs text-muted-foreground">
                  ({makerPct}% - 10%)
                </div>
              </div>
              <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <div className="text-muted-foreground mb-1">Taker Fee</div>
                <div className="text-lg font-bold text-emerald-500">
                  {takerDiscounted}%
                </div>
                <div className="text-xs text-muted-foreground">
                  ({takerPct}% - 10%)
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Example With Discount:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">
                  Position Size:
                </span>
                <span className="font-semibold">
                  ${exampleSize.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">
                  Standard Taker Fee:
                </span>
                <span className="font-semibold line-through">
                  ${takerFee.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <span className="font-semibold">With 10% Discount:</span>
                <span className="font-bold text-emerald-500">
                  ${takerFeeDiscounted.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold">You Save:</span>
                <span className="font-bold text-primary">
                  ${(takerFee - takerFeeDiscounted).toFixed(2)} per trade
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* When Fees Are Charged */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            When Fees Are Charged
          </h2>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Opening a Position</h3>
              <p className="text-sm text-muted-foreground">
                The trading fee is deducted from your collateral immediately
                when you open a position. Make sure you have sufficient
                balance including the fee.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Closing a Position</h3>
              <p className="text-sm text-muted-foreground">
                Another fee is charged when you close the position. This is
                deducted from the position's final value before calculating
                your realized P&L.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Fee Display</h3>
              <p className="text-sm text-muted-foreground">
                Before placing an order, you'll see the estimated fee
                displayed in the Order Form. The actual fee is calculated
                based on the exact fill price.
              </p>
            </div>
          </div>
        </section>

        {/* Other Costs */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            Other Trading Costs
          </h2>

          <p className="text-muted-foreground mb-6">
            In addition to trading fees, there are other costs to consider
            when trading perpetuals:
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Funding Rates</h3>
              <p className="text-sm text-muted-foreground">
                Charged every 8 hours on open positions. Can be positive
                (you pay) or negative (you receive) depending on market
                conditions.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Slippage</h3>
              <p className="text-sm text-muted-foreground">
                Market orders may execute at slightly different prices than
                displayed, especially during volatile periods or in
                low-liquidity conditions.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">
                Blockchain Transaction Fees
              </h3>
              <p className="text-sm text-muted-foreground">
                Deposits and withdrawals require small network
                fees that go to validators, not to Nordex. Solana fees are 
                typically less than $0.01. Ethereum and Arbitrum fees vary 
                with network congestion.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">
                Liquidation Penalty
              </h3>
              <p className="text-sm text-muted-foreground">
                If a position is liquidated, a penalty of 0.05% of the position value 
                is applied. This fee contributes to the platform's insurance fund. See{" "}
                <a href="/margin-liquidation" className="text-primary hover:underline">Margin & Liquidation</a> for 
                details.
              </p>
            </div>
          </div>
        </section>

        {/* Fee Transparency */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Fee Transparency</h2>

          <p className="text-muted-foreground mb-4">
            Nordex is committed to transparent fee structures:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              All fees are clearly displayed before you place an order
            </li>
            <li>
              You can view your trading fees in the trade history section
              of your Account page
            </li>
            <li>No hidden fees or surprise charges</li>
            <li>
              Referral discounts are automatically applied if you signed up
              with a referral link
            </li>
            <li>Fee structure is the same for all markets</li>
          </ul>
        </section>

        <DocsNavigation
          previous={{ title: "Available Markets", href: "/markets" }}
          next={{ title: "Account Overview", href: "/account" }}
        />
      </div>
    </DocsLayout>
  );
};
